import {
    Box,
    Text,
    Stack,
    Flex,
    useColorModeValue,
    VStack,
    HStack,
    Badge,
    List,
    ListItem,
    ListIcon,
    keyframes,
} from "@chakra-ui/react";
import { CheckCircleIcon } from "@chakra-ui/icons";
import { FaBriefcase, FaBuilding, FaMapMarkerAlt, FaCalendarAlt } from "react-icons/fa";

const fadeInUp = keyframes`
    from { opacity: 0; transform: translateY(40px); }
    to { opacity: 1; transform: translateY(0); }
`;

const slideInLeft = keyframes`
    from { opacity: 0; transform: translateX(-50px); }
    to { opacity: 1; transform: translateX(0); }
`;

const slideInRight = keyframes`
    from { opacity: 0; transform: translateX(50px); }
    to { opacity: 1; transform: translateX(0); }
`;

const pulseRing = keyframes`
    0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(20, 184, 166, 0.7); }
    70% { transform: scale(1); box-shadow: 0 0 0 20px rgba(20, 184, 166, 0); }
    100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(20, 184, 166, 0); }
`;

const glowPulse = keyframes`
    0%, 100% { filter: drop-shadow(0 0 5px rgba(20, 184, 166, 0.5)); }
    50% { filter: drop-shadow(0 0 20px rgba(20, 184, 166, 0.8)); }
`;

const float = keyframes`
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
`;

const shimmer = keyframes`
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
`;

const checkBounce = keyframes`
    0% { transform: scale(0); }
    50% { transform: scale(1.2); }
    100% { transform: scale(1); }
`;

const Experience = () => {
    const borderColor = useColorModeValue("gray.200", "gray.600");
    const textColor = useColorModeValue("gray.600", "gray.300");
    const timelineColor = useColorModeValue("teal.500", "teal.400");
    const cardBg = useColorModeValue("white", "gray.700");
    const buildingIconColor = useColorModeValue("teal.600", "teal.300");
    const companyColor = useColorModeValue("gray.800", "white");
    const roleColor = useColorModeValue("teal.600", "teal.300");
    const responsibilitiesTitleColor = useColorModeValue("gray.700", "gray.200");
    const technologiesTitleColor = useColorModeValue("gray.700", "gray.200");
    const labelBg = useColorModeValue("blue.50", "blue.900");
    const labelBorderColor = useColorModeValue("gray.300", "gray.600");
    const timelineLineColor = useColorModeValue("teal.200", "teal.700");
    const timelineGradientStart = useColorModeValue("#14b8a6", "#2dd4bf");
    const nodeBg = useColorModeValue("white", "gray.800");
    const iconBoxBg = useColorModeValue("teal.100", "teal.900");

    const experiences = [
        {
            id: 1,
            role: "Junior Frontend Developer",
            company: "FreshMenu",
            location: "Bengaluru, Karnataka",
            duration: "December 2024 - Present",
            type: "Full-time",
            description: "Working as a Junior Frontend Developer, building and maintaining responsive web applications using modern frontend technologies.",
            responsibilities: [
                "Developing responsive and interactive user interfaces using React.js",
                "Collaborating with the design team to implement pixel-perfect UI components",
                "Optimizing application performance and ensuring cross-browser compatibility",
                "Working with RESTful APIs and integrating backend services",
                "Participating in code reviews and following agile development practices"
            ],
            technologies: ["React.js", "JavaScript", "HTML5", "CSS3", "Git", "REST APIs"]
        }
    ];

    return (
        <Box width="100%" m="auto" padding={["5", "5", "10", "20"]}>
            <Stack spacing={8} w={["100%"]} m="auto">
                <Text
                    textTransform={"uppercase"}
                    color={"white.400"}
                    border="1px solid"
                    borderColor={labelBorderColor}
                    fontWeight={600}
                    fontSize={"sm"}
                    bg={labelBg}
                    p={2}
                    alignSelf={"flex-start"}
                    rounded={"md"}
                    animation={`${slideInLeft} 0.6s ease-out`}
                    position="relative"
                    overflow="hidden"
                    _before={{
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)",
                        animation: `${shimmer} 2s infinite`,
                    }}
                >
                    Work Experience
                </Text>

                <VStack spacing={10} align="stretch" position="relative">
                    {/* Animated Timeline Line */}
                    <Box
                        position="absolute"
                        left={{ base: "28px", md: "44px" }}
                        top="80px"
                        bottom="0"
                        width="3px"
                        bg={timelineLineColor}
                        borderRadius="full"
                        overflow="hidden"
                    >
                        <Box
                            width="100%"
                            height="100%"
                            bg={`linear-gradient(180deg, ${timelineGradientStart} 0%, transparent 100%)`}
                            animation={`${glowPulse} 2s ease-in-out infinite`}
                        />
                    </Box>

                    {experiences.map((exp, index) => (
                        <Flex
                            key={exp.id}
                            direction={{ base: "column", md: "row" }}
                            gap={6}
                            position="relative"
                        >
                            {/* Timeline Node with Enhanced Animation */}
                            <Box
                                position="relative"
                                zIndex={2}
                                animation={`${slideInLeft} 0.8s ease-out ${index * 0.2}s both`}
                            >
                                <Flex
                                    w={{ base: "56px", md: "88px" }}
                                    h={{ base: "56px", md: "88px" }}
                                    align={"center"}
                                    justify={"center"}
                                    rounded={"full"}
                                    bg={nodeBg}
                                    boxShadow="0 0 30px rgba(20, 184, 166, 0.3)"
                                    border="3px solid"
                                    borderColor={timelineColor}
                                    position="relative"
                                    animation={`${pulseRing} 2s ease-out ${index * 0.3}s infinite`}
                                    transition="all 0.3s ease"
                                    _hover={{
                                        transform: "scale(1.1)",
                                        boxShadow: "0 0 40px rgba(20, 184, 166, 0.5)",
                                    }}
                                >
                                    <FaBriefcase size={28} color={timelineColor} />
                                </Flex>
                                {/* Experience Badge */}
                                <Badge
                                    position="absolute"
                                    top="-8px"
                                    right="-30px"
                                    colorScheme="teal"
                                    fontSize="xs"
                                    px={2}
                                    py={0.5}
                                    borderRadius="full"
                                    animation={`${float} 3s ease-in-out ${index * 0.2}s infinite`}
                                >
                                    Present
                                </Badge>
                            </Box>

                            {/* Experience Card with Enhanced Effects */}
                            <Box
                                flex={1}
                                bg={cardBg}
                                p={{ base: 6, md: 8 }}
                                borderRadius="2xl"
                                borderWidth="1px"
                                borderColor={borderColor}
                                boxShadow="0 4px 20px rgba(0,0,0,0.08)"
                                position="relative"
                                overflow="hidden"
                                animation={`${slideInRight} 0.8s ease-out ${index * 0.2}s both`}
                                transition="all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
                                _before={{
                                    content: '""',
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "4px",
                                    height: "100%",
                                    bg: "teal.500",
                                    transform: "scaleY(0)",
                                    transition: "transform 0.4s ease",
                                }}
                                _hover={{
                                    transform: "translateY(-8px) translateX(5px)",
                                    boxShadow: "0 20px 40px rgba(20, 184, 166, 0.15)",
                                    borderColor: "teal.300",
                                    _before: {
                                        transform: "scaleY(1)",
                                    }
                                }}
                            >
                                {/* Decorative Background Element */}
                                <Box
                                    position="absolute"
                                    top="-50%"
                                    right="-20%"
                                    width="200px"
                                    height="200px"
                                    bg="teal.50"
                                    borderRadius="full"
                                    opacity={0.5}
                                    filter="blur(40px)"
                                    pointerEvents="none"
                                />

                                <Stack spacing={4} position="relative" zIndex={1}>
                                    {/* Header */}
                                    <Flex
                                        justify="space-between"
                                        align={{ base: "flex-start", md: "center" }}
                                        direction={{ base: "column", md: "row" }}
                                        gap={2}
                                    >
                                        <HStack spacing={3}>
                                            <Box
                                                p={2}
                                                bg={iconBoxBg}
                                                borderRadius="lg"
                                                animation={`${float} 4s ease-in-out infinite`}
                                            >
                                                <FaBuilding color={buildingIconColor} size={18} />
                                            </Box>
                                            <Text
                                                fontWeight={700}
                                                fontSize={{ base: "xl", md: "2xl" }}
                                                color={companyColor}
                                            >
                                                {exp.company}
                                            </Text>
                                        </HStack>
                                        <Badge
                                            colorScheme="teal"
                                            fontSize="sm"
                                            px={3}
                                            py={1}
                                            borderRadius="full"
                                            textTransform="uppercase"
                                            letterSpacing="wide"
                                            boxShadow="0 2px 10px rgba(20, 184, 166, 0.3)"
                                        >
                                            {exp.type}
                                        </Badge>
                                    </Flex>

                                    {/* Role & Duration */}
                                    <Box>
                                        <Text
                                            fontWeight={600}
                                            fontSize={{ base: "lg", md: "xl" }}
                                            color={roleColor}
                                            display="flex"
                                            alignItems="center"
                                            gap={2}
                                        >
                                            <Box 
                                                w={2} 
                                                h={2} 
                                                bg="teal.500" 
                                                borderRadius="full"
                                                animation={`${pulseRing} 2s infinite`}
                                            />
                                            {exp.role}
                                        </Text>
                                        <Flex
                                            fontSize="sm"
                                            color={textColor}
                                            mt={2}
                                            gap={4}
                                            flexWrap="wrap"
                                        >
                                            <HStack>
                                                <FaMapMarkerAlt color={timelineColor} />
                                                <Text>{exp.location}</Text>
                                            </HStack>
                                            <HStack>
                                                <FaCalendarAlt color={timelineColor} />
                                                <Text>{exp.duration}</Text>
                                            </HStack>
                                        </Flex>
                                    </Box>

                                    {/* Description */}
                                    <Text
                                        color={textColor}
                                        fontSize={{ base: "md", md: "lg" }}
                                        lineHeight="tall"
                                    >
                                        {exp.description}
                                    </Text>

                                    {/* Responsibilities */}
                                    <Box>
                                        <Text
                                            fontWeight={600}
                                            fontSize="md"
                                            mb={3}
                                            color={responsibilitiesTitleColor}
                                        >
                                            Key Responsibilities:
                                        </Text>
                                        <List spacing={3}>
                                            {exp.responsibilities.map((resp, idx) => (
                                                <ListItem
                                                    key={idx}
                                                    display="flex"
                                                    alignItems="flex-start"
                                                    animation={`${fadeInUp} 0.5s ease-out ${0.5 + idx * 0.1}s both`}
                                                    transition="all 0.3s ease"
                                                    _hover={{
                                                        transform: "translateX(10px)",
                                                    }}
                                                >
                                                    <ListIcon
                                                        as={CheckCircleIcon}
                                                        color="teal.500"
                                                        mt={1}
                                                        boxSize={5}
                                                        animation={`${checkBounce} 0.5s ease-out ${0.5 + idx * 0.1}s both`}
                                                    />
                                                    <Text color={textColor} fontSize="md">
                                                        {resp}
                                                    </Text>
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>

                                    {/* Technologies */}
                                    <Box pt={2}>
                                        <Text
                                            fontWeight={600}
                                            fontSize="md"
                                            mb={3}
                                            color={technologiesTitleColor}
                                        >
                                            Technologies Used:
                                        </Text>
                                        <Flex wrap="wrap" gap={2}>
                                            {exp.technologies.map((tech, idx) => (
                                                <Badge
                                                    key={idx}
                                                    colorScheme="blue"
                                                    variant="subtle"
                                                    fontSize="sm"
                                                    px={4}
                                                    py={1.5}
                                                    borderRadius="full"
                                                    cursor="default"
                                                    transition="all 0.3s ease"
                                                    animation={`${fadeInUp} 0.5s ease-out ${0.5 + idx * 0.1}s both`}
                                                    _hover={{
                                                        transform: "scale(1.1) rotate(2deg)",
                                                        boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
                                                        bg: "blue.500",
                                                        color: "white"
                                                    }}
                                                >
                                                    {tech}
                                                </Badge>
                                            ))}
                                        </Flex>
                                    </Box>
                                </Stack>
                            </Box>
                        </Flex>
                    ))}
                </VStack>
            </Stack>
        </Box>
    );
};

export default Experience;
